import { useEffect, useState } from "react";
import { boundaryCRUD } from "../../../actions/boundary";
import { dictionaryCRUD } from "../../../actions/dictionary";
import * as Yup from "yup";
import { useFormik } from "formik";
import { toast } from "react-toastify";
import { Loading } from "../../constant";
import Select from "react-select";

const boundarySchema = Yup.object().shape({
  boundary_code: Yup.string().required("Boundary code is required"),
  boundary_name: Yup.string().required("Boundary name is required"),
  boundary_initials: Yup.string().required("Boundary initials required"),
  postcode: Yup.string(),
  boundary_parent: Yup.number(),
  island_id: Yup.number().required("Island is required"),
  centroid_lat: Yup.number().nullable().min(-90, 'Latitude must be >= -90').max(90, 'Latitude must be <= 90'),
  centroid_lng: Yup.number().nullable().min(-180, 'Longitude must be >= -180').max(180, 'Longitude must be <= 180'),
  boundary_polygon: Yup.string().nullable(),
});

// Mapping of boundary level names to their dictionary_boundary_type IDs
const BOUNDARY_TYPE_IDS = {
  "Region": 21,
  "District": 22,
  "Locality": 23,
  "Shehia": 23,
  "Zone": 24,
};

export const EditBoundary = (props) => {
  const { dataId, setIndex, index, boundaryName, parentList } = props;
  const [loading, setLoading] = useState(false);
  // Ward/Street ids are environment-specific; resolve them by name.
  const [boundaryTypes, setBoundaryTypes] = useState([]);
  const resolveTypeId = () => {
    if (BOUNDARY_TYPE_IDS[boundaryName]) return BOUNDARY_TYPE_IDS[boundaryName];
    const match = boundaryTypes.find(
      (t) => (t.dictionary_item_name_en || "").toLowerCase() === boundaryName.toLowerCase()
    );
    return match ? match.id : 0;
  };
  useEffect(() => {
    dictionaryCRUD
      .loadDictionaryData(100, 0, "", 2)
      .then((resp) => setBoundaryTypes(resp.data.results))
      .catch(() => {});
  }, []);
  const [dataLoading, setDataLoading] = useState(false);
  const [selectedParent, setSelectedParent] = useState(null);
  const [selectedIsland, setSelectedIsland] = useState(null);
  const [islandList, setIslandList] = useState([]);

  // Load islands from backend
  useEffect(() => {
    boundaryCRUD
      .loadAllIslands()
      .then((resp) => {
        setIslandList(resp.data);
      })
      .catch((error) => {
        console.log(error);
      });
  }, []);

  const initialValues = {
    boundary_code: "",
    boundary_name: "",
    boundary_initials: "",
    postcode: "",
    boundary_parent: 0,
    island_id: 0,
    centroid_lat: "",
    centroid_lng: "",
    centroid_coords: "",
    boundary_polygon: "",
  };

  const formik = useFormik({
    initialValues,
    validationSchema: boundarySchema,
    onSubmit: (values, { setSubmitting }) => {
      const {
        boundary_code,
        boundary_name,
        boundary_initials,
        postcode,
        boundary_parent,
        island_id,
        centroid_lat,
        centroid_lng,
        boundary_polygon,
      } = values;

      // Auto-set boundary type based on the level being edited
      const dictionary_boundary_type = resolveTypeId();

      const data = {
        boundary_code,
        boundary_name,
        boundary_initials,
        postcode: postcode || null,
        boundary_parent: boundary_parent || null,
        dictionary_boundary_type,
        island_id,
        centroid_lat: centroid_lat !== '' ? parseFloat(centroid_lat) : null,
        centroid_lng: centroid_lng !== '' ? parseFloat(centroid_lng) : null,
        boundary_polygon: boundary_polygon || null,
      };

      setLoading(true);
      setTimeout(() => {
        boundaryCRUD
          .updateBoundary(dataId, data)
          .then(() => {
            toast.success(`${boundaryName} has been updated.`, {
              autoClose: 7000,
            });
            setIndex(index + 1);
            setSubmitting(false);
            setLoading(false);
          })
          .catch((error) => {
            toast.error(error.message || `Failed to update ${boundaryName}`, {
              autoClose: 7000,
            });
            setSubmitting(false);
            setLoading(false);
          });
      }, 500);
    },
  });

  // Load boundary data when dataId changes
  useEffect(() => {
    if (!dataId) return;

    setDataLoading(true);
    setSelectedIsland(null);
    setSelectedParent(null);

    setTimeout(() => {
      boundaryCRUD
        .loadBoundaryById(dataId)
        .then((resp) => {
          const data = resp.data;
          formik.setFieldValue("boundary_code", data.boundary_code || "");
          formik.setFieldValue("boundary_name", data.boundary_name || "");
          formik.setFieldValue("boundary_initials", data.boundary_initials || "");
          formik.setFieldValue("postcode", data.postcode || "");
          formik.setFieldValue("island_id", data.island_id || 0);
          formik.setFieldValue("boundary_parent", data.boundary_parent || 0);
          formik.setFieldValue("centroid_lat", data.centroid_lat !== null ? data.centroid_lat : "");
          formik.setFieldValue("centroid_lng", data.centroid_lng !== null ? data.centroid_lng : "");
          // Pre-fill the single combined field.
          if (data.centroid_lat !== null && data.centroid_lng !== null) {
            formik.setFieldValue("centroid_coords", `${data.centroid_lat}, ${data.centroid_lng}`);
          }
          formik.setFieldValue("boundary_polygon", data.boundary_polygon || "");

          // Set selected values for dropdowns
          if (data.island_id && islandList.length > 0) {
            const island = islandList.find((i) => i.id === data.island_id);
            setSelectedIsland(island ? { value: island.id, label: island.name } : null);
          }

          if (data.boundary_parent && parentList) {
            const parent = parentList.find((p) => p.id === data.boundary_parent);
            setSelectedParent(parent ? { value: parent.id, label: parent.boundary_name } : null);
          }
        })
        .finally(() => {
          setDataLoading(false);
        });
    }, 500);
  }, [dataId, islandList]);

  return (
    <>
      <div
        className="modal fade editBoundary"
        tabIndex="-1"
        role="dialog"
        aria-labelledby="editBoundaryModalLabel"
        aria-hidden="true"
      >
        <div className="modal-dialog modal-lg">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title" id="editBoundaryModalLabel">
                Edit {boundaryName}
              </h5>
              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
              ></button>
            </div>
            <hr />
            <form onSubmit={formik.handleSubmit}>
              {dataLoading ? (
                <Loading />
              ) : (
                <div className="modal-body">
                  <div className="row">
                    <div className="col-md-4 col-sm-12">
                      <div className="mb-3">
                        <label htmlFor="boundary_code" className="form-label">
                          Boundary Code <span className="text-danger">*</span>
                        </label>
                        <input
                          type="text"
                          id="boundary_code"
                          {...formik.getFieldProps("boundary_code")}
                          className="form-control"
                          placeholder="Boundary Code"
                        />
                        {formik.touched.boundary_code &&
                          formik.errors.boundary_code && (
                            <div
                              className="fw-light h6 mt-1"
                              style={{ fontSize: "15px" }}
                            >
                              <span role="alert" className="text-danger">
                                {formik.errors.boundary_code}
                              </span>
                            </div>
                          )}
                      </div>
                    </div>

                    <div className="col-md-4 col-sm-12">
                      <div className="mb-3">
                        <label htmlFor="boundary_name" className="form-label">
                          Boundary Name <span className="text-danger">*</span>
                        </label>
                        <input
                          type="text"
                          id="boundary_name"
                          {...formik.getFieldProps("boundary_name")}
                          className="form-control"
                          placeholder="Boundary Name"
                        />
                        {formik.touched.boundary_name &&
                          formik.errors.boundary_name && (
                            <div
                              className="fw-light h6 mt-1"
                              style={{ fontSize: "15px" }}
                            >
                              <span role="alert" className="text-danger">
                                {formik.errors.boundary_name}
                              </span>
                            </div>
                          )}
                      </div>
                    </div>

                    <div className="col-md-4 col-sm-12">
                      <div className="mb-3">
                        <label htmlFor="boundary_initials" className="form-label">
                          Boundary Initials <span className="text-danger">*</span>
                        </label>
                        <input
                          type="text"
                          id="boundary_initials"
                          {...formik.getFieldProps("boundary_initials")}
                          className="form-control"
                          placeholder="Boundary Initials"
                        />
                        {formik.touched.boundary_initials &&
                          formik.errors.boundary_initials && (
                            <div
                              className="fw-light h6 mt-1"
                              style={{ fontSize: "15px" }}
                            >
                              <span role="alert" className="text-danger">
                                {formik.errors.boundary_initials}
                              </span>
                            </div>
                          )}
                      </div>
                    </div>
                  </div>

                  <div className="row">
                    <div className="col-md-4 col-sm-12">
                      <div className="mb-3">
                        <label htmlFor="postcode" className="form-label">
                          Postcode
                        </label>
                        <input
                          type="text"
                          id="postcode"
                          {...formik.getFieldProps("postcode")}
                          className="form-control"
                          placeholder="Postcode"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="row">
                    <div className="col-md-4 col-sm-12">
                      <div className="mb-3">
                        <label htmlFor="island_id" className="form-label">
                          Island <span className="text-danger">*</span>
                        </label>
                        <Select
                          value={selectedIsland}
                          options={islandList.map((dt) => ({
                            value: dt.id,
                            label: dt.name,
                          }))}
                          onChange={(e) => {
                            formik.setFieldValue("island_id", e.value);
                            setSelectedIsland(e);
                          }}
                          isSearchable={true}
                        />
                      </div>
                    </div>

                    <div className="col-md-4 col-sm-12">
                      <div className="mb-3">
                        <label htmlFor="boundary_parent" className="form-label">
                          Boundary Parent
                        </label>
                        <Select
                          value={selectedParent}
                          options={parentList?.map((dt) => ({
                            value: dt.id,
                            label: dt.boundary_name,
                          }))}
                          onChange={(e) => {
                            formik.setFieldValue("boundary_parent", e ? e.value : null);
                            setSelectedParent(e);
                          }}
                          isSearchable={true}
                          isClearable={true}
                        />
                      </div>
                    </div>

                    <div className="col-md-4 col-sm-12">
                      <div className="mb-3">
                        <label htmlFor="dictionary_boundary_type" className="form-label">
                          Boundary Type
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          value={boundaryName}
                          disabled
                          readOnly
                        />
                        <small className="text-muted">Auto-determined based on level</small>
                      </div>
                    </div>
                  </div>

                  <div className="row">
                    <div className="col-md-12">
                      <h6 className="text-muted mb-3">Boundary Centroid</h6>
                    </div>
                    <div className="col-md-12 col-sm-12">
                      <div className="mb-3">
                        <label htmlFor="centroid_coords" className="form-label">
                          Centroid (Latitude, Longitude)
                        </label>
                        <input
                          type="text"
                          id="centroid_coords"
                          value={formik.values.centroid_coords}
                          onChange={(e) => {
                            const raw = e.target.value;
                            formik.setFieldValue("centroid_coords", raw);
                            const parts = raw.split(/[,\s]+/).map((p) => p.trim()).filter(Boolean);
                            if (parts.length === 2 && !isNaN(parts[0]) && !isNaN(parts[1])) {
                              formik.setFieldValue("centroid_lat", parts[0]);
                              formik.setFieldValue("centroid_lng", parts[1]);
                            } else {
                              formik.setFieldValue("centroid_lat", "");
                              formik.setFieldValue("centroid_lng", "");
                            }
                          }}
                          className="form-control"
                          placeholder="e.g. -6.1659, 39.2026"
                        />
                        <small className="text-muted">
                          Paste as <strong>Latitude, Longitude</strong> (e.g. -6.1659, 39.2026) — same order as Google Maps.
                        </small>
                      </div>
                    </div>
                  </div>

                  <div className="row">
                    <div className="col-md-12">
                      <div className="mb-3">
                        <label className="form-label">Boundary Polygon</label>
                        <textarea
                          id="boundary_polygon"
                          {...formik.getFieldProps("boundary_polygon")}
                          className="form-control"
                          rows="4"
                          placeholder="Paste GeoJSON or WKT polygon data here..."
                        />
                        <small className="text-muted">Paste MultiPolygon geometry in GeoJSON or WKT format</small>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              <hr />
              <div className="modal-footer">
                <a
                  href="javascript:void(0);"
                  className="btn btn-soft-danger fw-medium"
                  data-bs-dismiss="modal"
                >
                  <i className="ri-close-line me-1 align-middle"></i> Close
                </a>
                <button
                  type="submit"
                  className="btn btn-soft-success"
                  disabled={formik.isSubmitting || !formik.isValid}
                >
                  {!loading && (
                    <span className="indicator-label">
                      <i className="las la-save la-lg"></i> Save
                    </span>
                  )}
                  {loading && (
                    <span
                      className="indicator-progress"
                      style={{ display: "block" }}
                    >
                      Please wait...
                      <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
                    </span>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
};
